import React from 'react';
import { Question } from '../../../types/game';
import { QuestionHint } from '../QuestionHint/QuestionHint';
import { FlipCard } from '../FlipCard/FlipCard';
import { DrunkQuestionText } from '../DrunkQuestionText/DrunkQuestionText';
import { getQuestionId } from '../../../utils/gameUtils';
import './QuestionDisplay.css';

interface QuestionDisplayProps {
  question: Question;
  showQuestionFlip: boolean;
  isFadingOut: boolean;
  previousQuestionIdForFlipRef: React.MutableRefObject<string | undefined>;
  showHint: boolean;
  gameType: string;
  /** Optional meme URL to show on flip back (e.g. Tech Trivia) */
  memeUrl?: string | null;
  /** When true, question text is rendered with letter animations (Are You Drunk / Vision Check) */
  drunkVision?: boolean;
}

export const QuestionDisplay: React.FC<QuestionDisplayProps> = ({
  question,
  showQuestionFlip,
  isFadingOut,
  previousQuestionIdForFlipRef,
  showHint,
  gameType,
  memeUrl,
  drunkVision,
}) => {
  const currentQuestionId = getQuestionId(question);
  const hasBackContent = (question.interesting || memeUrl) && (showQuestionFlip || isFadingOut) &&
    currentQuestionId === previousQuestionIdForFlipRef.current;

  const questionClassName = `game-question-text ${question.isPicturePerfect ? 'picture-perfect' : ''}`;
  const letterVariant = gameType === 'vision-check' ? 'vision' : 'drunk';
  const front = drunkVision ? (
    <DrunkQuestionText
      text={question.question}
      className={questionClassName}
      variant={letterVariant}
    />
  ) : (
    <h1 className={questionClassName}>
      {question.question}
    </h1>
  );

  const back = hasBackContent ? (
    <div className="question-display__back-content">
      {memeUrl && (
        <div className="question-display__meme-wrap">
          <img src={memeUrl} alt="" className="question-display__meme-img" />
        </div>
      )}
      {question.interesting && (
        <>
          <p className="question-display__did-you-know-label">
            💡 Did you know?
          </p>
          <p className={`game-did-you-know-text ${question.isPicturePerfect ? 'picture-perfect' : ''}`}>
            {question.interesting}
          </p>
        </>
      )}
    </div>
  ) : null;

  return (
    <div className="question-display">
      {/* key remounts on new question so we never animate out of a leftover "Did you know?" flip */}
      <FlipCard
        key={currentQuestionId || 'question'}
        flipped={showQuestionFlip}
        isFadingOut={isFadingOut}
        front={
          <>
            {front}
            {showHint && question.hint && (gameType === 'collaborative-puzzle' || gameType === 'story-chain') && (
              <QuestionHint hint={question.hint} variant="question" />
            )}
          </>
        }
        back={back}
        className="question-display__flip"
      />
    </div>
  );
};
